/**
 * The single fetch wrapper every API call goes through.
 *
 * Unwraps the `{ data, meta }` envelope, turns `{ error }` bodies into a typed
 * ApiError carrying the server's machine-readable `code`, and preserves `details`
 * so the UI can act on a failure (offer to start the worker, jump to the manifest
 * editor) instead of just printing a red string.
 */

const BASE = import.meta.env.VITE_API_BASE || '/api/v1';

/** Codes that mean "the same request will work if you send it again shortly". */
const TRANSIENT_CODES = [
  'worker_busy',
  'worker_unavailable',
  'inference_failed',
  // vLLM serving: every slot stayed busy for the whole admission wait (429).
  'capacity_exceeded',
  // vLLM serving: the base model is still loading after an administrator's Load.
  'model_starting',
  'model_server_unavailable',
];

export class ApiError extends Error {
  constructor(
    message,
    { code = 'unknown', status = 0, details = {}, requestId = '', retryAfter = 0 } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
    /** Seconds the server asked us to wait before retrying (Retry-After), or 0. */
    this.retryAfter = retryAfter;
  }

  /** Failures worth a retry button rather than a correction. */
  get isTransient() {
    return TRANSIENT_CODES.includes(this.code);
  }

  /** The version's base model is not on the GPU; an administrator has to load it. */
  get isModelNotLoaded() {
    return this.code === 'model_not_loaded';
  }

  get isWorkerDown() {
    return this.code === 'worker_unavailable';
  }

  /** The session expired or was revoked — the app should return to the login screen. */
  get isUnauthenticated() {
    return this.code === 'unauthorized' || this.status === 401;
  }

  /** Signed in, but not permitted. Different remedy: ask an admin, do not re-login. */
  get isForbidden() {
    return this.code === 'forbidden' || this.status === 403;
  }

  get needsVersionSetup() {
    return this.code === 'version_not_ready';
  }
}

function apiUrl(path) {
  return path.startsWith('http') ? path : `${BASE}${path}`;
}

async function toError(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    /* non-JSON error body (proxy or gateway) */
  }
  const error = payload?.error;
  const retryAfter =
    Number(response.headers.get('Retry-After')) || Number(error?.details?.retry_after_s) || 0;
  // nginx answers its own rate limit with a bare 429/503 and no JSON body.
  const fallbackCode = response.status === 429 ? 'capacity_exceeded' : `http_${response.status}`;
  return new ApiError(error?.message || defaultMessage(response.status), {
    code: error?.code || fallbackCode,
    status: response.status,
    details: error?.details || {},
    requestId: payload?.meta?.request_id || response.headers.get('X-Request-Id') || '',
    retryAfter,
  });
}

function defaultMessage(status) {
  if (status === 429) return 'The service is at capacity. Try again in a few seconds.';
  return `Request failed (HTTP ${status})`;
}

async function request(path, { method = 'GET', body, signal, headers = {} } = {}) {
  const isFormData = body instanceof FormData;
  let response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      signal,
      // The session cookie is HttpOnly, so it only travels if we ask for it.
      // In dev the UI is on :5173 and the API on :8000 — a cross-origin request,
      // where fetch omits cookies unless told otherwise. Without this, every
      // request after login is a 401 and the cause is invisible in the UI.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(isFormData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    throw new ApiError('Cannot reach the API. Is the backend running on port 8000?', {
      code: 'network_error',
    });
  }

  if (response.status === 204) return null;
  if (!response.ok) throw await toError(response);

  const payload = await response.json();
  return payload?.data !== undefined ? payload.data : payload;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),

  /** Full envelope, when the caller needs `meta` (pagination, version_id). */
  async getWithMeta(path, options) {
    const response = await fetch(apiUrl(path), {
      ...options,
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw await toError(response);
    return response.json();
  },

  /** Absolute URL for <img src> and download links. */
  url: apiUrl,

  /** Trigger a browser download without leaving the page. */
  download(path, filename) {
    const link = document.createElement('a');
    link.href = apiUrl(path);
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  },
};
